import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  Student, 
  Activity,
  DailyReport,
  CreateStudentRequest,
  UpdateStudentRequest,
  ListQueryParams,
  ActivityQueryParams 
} from '../types/api';
import { 
  extractListData, 
  extractSingleData,
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse
} from '../utils/railsResponseHandler';
import { withQueryErrorHandling } from '../utils/apiErrorHandler';

// Student CRUD operations
export const getStudents = async (params?: ListQueryParams): Promise<{ students: Student[]; pagination: any }> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsWrappedListResponse<Student>>(getApiUrl('/students'), { params });
    
    const students = extractListData<Student>(response.data);
    const pagination = extractPagination(response.data);
    
    return {
      students,
      pagination
    };
  });
};

// Get students for a specific school
export const getSchoolStudents = async (
  schoolId: string,
  params?: ListQueryParams
): Promise<{ students: Student[]; pagination: any }> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsWrappedListResponse<Student>>(
      getApiUrl(`/schools/${schoolId}/students`), 
      { params }
    );
    
    const students = extractListData<Student>(response.data);
    const pagination = extractPagination(response.data);
    
    return {
      students,
      pagination
    };
  });
};

export const getStudent = async (id: string, view?: string): Promise<Student> => {
  return withQueryErrorHandling(async () => {
    const params = view ? { view } : {};
    const response = await apiClient.get<RailsSingleWrappedResponse<Student>>(
      getApiUrl(`/students/${id}`), 
      { params }
    );
    
    const student = extractSingleData<Student>(response.data);
    
    // Skip validation for now due to schema complexity
    // TODO: Implement proper validation once OpenAPI types are generated
    return student;
  });
};

export const createStudent = async (data: CreateStudentRequest): Promise<Student> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.post<RailsSingleWrappedResponse<Student>>(
      getApiUrl('/students'), 
      data
    );
    
    return extractSingleData<Student>(response.data);
  });
};

// Create student for a specific school
export const createSchoolStudent = async (
  schoolId: string,
  data: CreateStudentRequest
): Promise<Student> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.post<RailsSingleWrappedResponse<Student>>(
      getApiUrl(`/schools/${schoolId}/students`), 
      data
    );
    
    return extractSingleData<Student>(response.data);
  });
};

export const updateStudent = async (id: string, data: UpdateStudentRequest): Promise<Student> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.put<RailsSingleWrappedResponse<Student>>(
      getApiUrl(`/students/${id}`), 
      data
    );
    
    return extractSingleData<Student>(response.data);
  });
};

export const deleteStudent = async (id: string): Promise<void> => {
  return withQueryErrorHandling(async () => {
    await apiClient.delete(getApiUrl(`/students/${id}`));
  });
};

// Face recognition operations
export const uploadStudentPhotos = async (id: string, photos: File[]): Promise<{ photos: any[] }> => {
  return withQueryErrorHandling(async () => {
    const formData = new FormData();
    photos.forEach((photo) => {
      formData.append(`photos`, photo);
    });

    const response = await apiClient.post<RailsSingleWrappedResponse<{ photos: any[] }>>(
      getApiUrl(`/students/${id}/upload_photos`), 
      formData, 
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return extractSingleData<{ photos: any[] }>(response.data);
  });
};

export const getFaceRecognitionStatus = async (id: string): Promise<any> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsSingleWrappedResponse<any>>(getApiUrl(`/students/${id}/face_recognition_status`));
    return extractSingleData<any>(response.data);
  });
};

export const trainFaceRecognition = async (id: string): Promise<any> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.post<RailsSingleWrappedResponse<any>>(getApiUrl(`/students/${id}/train_face_recognition`));
    return extractSingleData<any>(response.data);
  });
};

// Student activities
export const getStudentActivities = async (
  id: string, 
  params?: ActivityQueryParams
): Promise<{ activities: Activity[]; pagination: any }> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsWrappedListResponse<Activity>>(
      getApiUrl(`/students/${id}/activities`), 
      { params }
    );
    
    const activities = extractListData<Activity>(response.data);
    const pagination = extractPagination(response.data);
    
    return {
      activities,
      pagination
    };
  });
};

// Student daily reports
export const getStudentDailyReport = async (id: string, date?: string): Promise<DailyReport> => {
  return withQueryErrorHandling(async () => {
    const params = date ? { date } : {};
    const response = await apiClient.get<RailsSingleWrappedResponse<DailyReport>>(
      getApiUrl(`/students/${id}/daily_report`), 
      { params }
    );
    
    return extractSingleData<DailyReport>(response.data);
  });
};

// Student attendance
export const getStudentAttendance = async (
  id: string, 
  params?: { start_date?: string; end_date?: string }
): Promise<any> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsSingleWrappedResponse<any>>(
      getApiUrl(`/students/${id}/attendance`), 
      { params }
    );
    
    return extractSingleData<any>(response.data);
  });
};